import type {
  WorkflowRunStepOperation,
  WorkflowRunTask,
} from "@/store/workflowRunStore";
import { AgentWorkflowRunStepOperationType } from "@/store/workflowRunStore";
import type {
  AgentSdkMessage,
  SendEmailRequest,
  TaskActionId,
} from "@forgepoint/shared";
import { isSendEmailAction } from "@forgepoint/shared";

export const RICH_ACTIONS = [
  "validate_quote_action",
  "finalize_quote_action",
  "validate_invoice_action",
  "validate_design_files_action",
  "user_review_action",
  "send_email_action",
  "email_send_action",
  "gmail_send_mail_forgepoint",
  "smtp_send_email_forgepoint",
  "microsoft_outlook_send_mail_forgepoint",
] as const;

export type RichActionType = (typeof RICH_ACTIONS)[number];

/** Set of all email send action IDs, derived from the shared membership. */
export const EMAIL_SEND_ACTION_IDS: ReadonlySet<RichActionType> = new Set(
  RICH_ACTIONS.filter(isSendEmailAction),
);

/** Checks whether a rich action type is an email send action. */
export const isEmailSendAction = (actionType: RichActionType): boolean => {
  return EMAIL_SEND_ACTION_IDS.has(actionType);
};

export interface RichActionTask {
  id: string;
  status: string;
  assignees?: Array<{
    id: string;
    assigneeUserId: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string;
  }>;
}

export interface RichActionData {
  richActionType: RichActionType;
  actionArguments: Record<string, unknown>;
  operationId?: string;
  task?: RichActionTask;
  /** Error message when the action failed to execute. */
  error?: string | null;
}

export const parseRichActionType = (
  actionType: string,
): RichActionType | undefined => {
  return (
    RICH_ACTIONS.find((richAction) => richAction === actionType) || undefined
  );
};

export const getRichActionDisplayName = (
  actionType: RichActionType,
): string => {
  switch (actionType) {
    case "validate_quote_action":
      return "Validate Quote";
    case "finalize_quote_action":
      return "Finalize Quote";
    case "validate_invoice_action":
      return "Validate Invoice";
    case "validate_design_files_action":
      return "Validate Design Files";
    case "user_review_action":
      return "User Review";
    case "send_email_action":
    case "email_send_action":
    case "gmail_send_mail_forgepoint":
    case "smtp_send_email_forgepoint":
    case "microsoft_outlook_send_mail_forgepoint":
      return "Send Email";
    default:
      return actionType;
  }
};

/**
 * Humanized label for a task action id (e.g. `FinalizeQuote` → `Finalize
 * quote`). Used on task rows and panels to tell the user what the
 * task's attached action will do when approved.
 * @param actionId - The task action id
 * @returns The display label for the action
 */
export const getTaskActionDisplayName = (actionId: TaskActionId): string => {
  switch (actionId) {
    case "FinalizeQuote":
      return "Finalize quote";
    case "PreviewQuote":
      return "Preview quote";
    case "SendEmail":
      return "Send email";
  }
};

/** How a SendEmail request sends: as a new email, or continuing a thread. */
export type SendEmailMode = "new" | "reply" | "replyAll" | "forward";

/** Review labels for a SendEmail mode: its thread label and the text shown for fields the thread fills in. */
export interface SendEmailModeLabels {
  thread: string | null;
  subject: string;
  to: string;
  cc: string;
}

/**
 * Derives how a SendEmail request sends from the targets the agent set.
 * @param request - The SendEmail request under review
 * @returns The send mode
 */
export const getSendEmailMode = (request: SendEmailRequest): SendEmailMode => {
  if (request.forwardMessageId) {
    return "forward";
  }
  if (request.replyToMessageId && request.replyAll) {
    return "replyAll";
  }
  if (request.replyToMessageId) {
    return "reply";
  }
  return "new";
};

/**
 * Review labels for a SendEmail mode, naming what the thread fills in when the request leaves it out.
 * @param mode - The request's send mode
 * @returns The thread label (null for a new email) and the empty subject, To and CC labels
 */
export const getSendEmailModeLabels = (
  mode: SendEmailMode,
): SendEmailModeLabels => {
  switch (mode) {
    case "new":
      return { thread: null, subject: "No subject", to: "None", cc: "None" };
    case "reply":
      return {
        thread: "Reply on the original thread",
        subject: "Thread subject",
        to: "Thread recipients",
        cc: "None",
      };
    case "replyAll":
      return {
        thread: "Reply all on the original thread",
        subject: "Thread subject",
        to: "Thread recipients",
        cc: "Thread recipients",
      };
    case "forward":
      return {
        thread: "Forward of a message on the thread",
        subject: "Thread subject",
        to: "None",
        cc: "None",
      };
  }
};

/**
 * Whether an email address is external: not an org member and not on an internal domain.
 * @param address - The recipient address
 * @param internalDomains - The organization's internal email domains
 * @param memberEmails - Lower-cased email addresses of org members
 * @returns True when the address is outside the organization
 */
export const isExternalEmailAddress = (
  address: string,
  internalDomains: string[],
  memberEmails: ReadonlySet<string>,
): boolean => {
  const email = address.toLowerCase();
  if (memberEmails.has(email)) {
    return false;
  }
  const domain = email.split("@")[1];
  return !internalDomains.some(
    (internalDomain) => internalDomain.toLowerCase() === domain,
  );
};

/**
 * Returns the subtitle text for a rich action card.
 * For email actions, shows the recipient and subject. For others, shows "Interactive preview".
 */
export const getRichActionSubtitle = (
  actionType: RichActionType,
  actionArguments: Record<string, unknown>,
): string => {
  if (!isEmailSendAction(actionType)) {
    return "Interactive preview";
  }
  const recipient = resolveEmailRecipientLabel(actionArguments);
  const subject =
    typeof actionArguments.subject === "string"
      ? actionArguments.subject.trim()
      : "";

  const MAX_LENGTH = 60;
  const parts: string[] = [];
  if (recipient) {
    parts.push(`To: ${recipient}`);
  }
  if (subject) {
    parts.push(subject);
  }
  if (parts.length === 0) {
    return "Email preview";
  }
  const combined = parts.join(" · ");
  return combined.length > MAX_LENGTH
    ? combined.slice(0, MAX_LENGTH) + "..."
    : combined;
};

/**
 * Extracts a display-friendly recipient label from email action arguments.
 * Handles both `to` (string | string[]) and `recipients` (string[]) field names.
 */
const resolveEmailRecipientLabel = (
  args: Record<string, unknown>,
): string | undefined => {
  const toRaw = args.to ?? args.recipients;
  const recipients = Array.isArray(toRaw)
    ? toRaw.filter((v): v is string => typeof v === "string")
    : typeof toRaw === "string"
      ? [toRaw]
      : [];
  if (recipients.length === 0) {
    return undefined;
  }
  if (recipients.length === 1) {
    return recipients[0];
  }
  return `${recipients[0]} +${recipients.length - 1}`;
};

/**
 * Extracts an error message from a tool_call_result message, if present.
 * Tool call errors are returned as result strings prefixed with "Error:".
 */
export const extractToolCallError = (
  toolResultMessage: AgentSdkMessage | undefined,
): string | null => {
  if (!toolResultMessage) {
    return null;
  }
  const msg = toolResultMessage.message;
  if (msg.type !== "tool_call_result") {
    return null;
  }
  if (typeof msg.result === "string" && msg.result.startsWith("Error:")) {
    return msg.result;
  }
  return null;
};

export const mapOperationToRichActionData = (
  operation: WorkflowRunStepOperation,
  task?: WorkflowRunTask,
): RichActionData | null => {
  if (
    operation.operationParameters.type !==
    AgentWorkflowRunStepOperationType.TOOL_CALL
  ) {
    return null;
  }

  const richActionType = parseRichActionType(
    operation.operationParameters.actionId,
  );
  if (!richActionType) {
    return null;
  }

  const input =
    typeof operation.operationParameters.input === "object" &&
    operation.operationParameters.input !== null
      ? (operation.operationParameters.input as Record<string, unknown>)
      : {};

  const richActionTask: RichActionTask | undefined = task
    ? {
        id: task.id,
        status: task.status,
        assignees: task.assignees,
      }
    : undefined;

  const error =
    operation.operationParameters.type ===
    AgentWorkflowRunStepOperationType.TOOL_CALL
      ? operation.operationParameters.error
      : null;

  return {
    richActionType,
    actionArguments: input,
    operationId: operation.id,
    task: richActionTask,
    error,
  };
};

export const mapSdkMessageToRichActionData = (
  message: AgentSdkMessage,
  error?: string | null,
): RichActionData | null => {
  if (message.message.type !== "tool_call") {
    return null;
  }

  const richActionType = parseRichActionType(message.message.name);
  if (!richActionType) {
    return null;
  }

  const richActionTask: RichActionTask | undefined = message.task
    ? {
        id: message.task.id,
        status: message.task.status,
        assignees: message.task.assignees,
      }
    : undefined;

  return {
    richActionType,
    actionArguments: message.message.arguments,
    task: richActionTask,
    error,
  };
};
